/* Core & libraries */
import React, { useState } from "react";
import { useTheme } from "react-jss";
import { Link, useNavigate } from "react-router-dom";
import { NumericFormat } from 'react-number-format';
import { FormattedMessage } from "react-intl";

/* Helpers, constants, custom methods, internal libraries */
import { REPORTS_BOOKING_ROUTE } from "../../../../../constants/routes";
/* Components */
import UCGrid from "../../../../ucgrid";
import ReportsNoBookings from "../../../../reportsNoBookings"

/* Styles */
import styles from "./styles.js"

// to be changed for booking grid

const getReasonCodeDescription = (reasonCode) => {
  switch (reasonCode) {
    case 'RC01':
      return <FormattedMessage id="bookingsGrid.reasonCode.RC01"/>
    case 'RC02':
      return <FormattedMessage id="bookingsGrid.reasonCode.RC02"/>
    case 'RC03':
      return <FormattedMessage id="bookingsGrid.reasonCode.RC03"/>
    case 'RC04':
      return <FormattedMessage id="bookingsGrid.reasonCode.RC04"/>
    case 'RC05':
      return <FormattedMessage id="bookingsGrid.reasonCode.RC05"/>
    default:
      break;
  }
}

const getNameParts = (bookedByfullName) => {
  if (!bookedByfullName) return ["-", "-"];

  const parts = bookedByfullName.trim().split(/\s+/);
  const firstName = parts[0] || "-";
  const lastName = parts.length > 1 ? parts[parts.length - 1] : "-";

  return [firstName, lastName];
};

const BookingGrid = (props) => {
  const {gridOptions, setGridOptions, bookingsResponse, onRowClick, handleSort, defaultSort} = props;
  const theme = useTheme();
  const classes = styles({ theme });
  const navigate = useNavigate();

  const [selectedRow, setSelectedRow] = useState(null);

  return (
    <div className={classes.bookingsGrid}>
      <UCGrid
        gridName="bookingsGrid"
        data={bookingsResponse?.results}
        columns={bookingsResponse?.columns}
        actions={bookingsResponse?.actions}
        handlePageChange={(page) => {
          setGridOptions({
            ...gridOptions,
            page,
          });
          page > 1
            ? navigate(`/app/reports/bookings?page=${page}`)
            : navigate(`/app/reports/bookings`);
        }}
        setSelectedRow={setSelectedRow}
        spinner="bookingsGridSpinner"
        customColumns={{
          parentName: (row) => (
            <Link
              to={`${'../../' + REPORTS_BOOKING_ROUTE}/${row.parentID}`}
              className={classes.link}
            >
              {row.parentName}
            </Link>
          ),
          passengerName: (row) => {
            const name = `${row.firstName} ${row.lastName}`;
            return (
              <div className={classes.details}>
                <p>{name}</p>
              </div>
            );
          },
          baseFare: (row) => {
            return (
              <div className={classes.details}>
                <NumericFormat
                  value={row?.baseFare}
                  thousandSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  displayType="text"
                  renderText={(value) => <div>{value}</div>}
                />
              </div>
            );
          },
          totalFare: (row) => {
            return (
              <div className={classes.details}>
                <NumericFormat
                  value={row?.totalFare}
                  thousandSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  displayType="text"
                  renderText={(value) => <div>{value}</div>}
                />
              </div>
            );
          },
          totalTaxes: (row) => {
            return (
              <div className={classes.details}>
                <NumericFormat
                  value={row?.totalTaxes}
                  thousandSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  displayType="text"
                  renderText={(value) => <div>{value}</div>}
                />
              </div>
            );
          },
          ancillaryTotal: (row) => {
            return (
              <div className={classes.details}>
                <NumericFormat
                  value={row?.ancillaryTotal}
                  thousandSeparator=","
                  decimalScale={2}
                  fixedDecimalScale
                  displayType="text"
                  renderText={(value) => <div>{value}</div>}
                />
              </div>
            );
          },
          status: (row) => {
            return row.status && <FormattedMessage id={`bookingsGrid.${row.status}`} />;
          },
          price: (row) => {
            return row.totalPrice;
          },
          bookingChannel: (row) => {
            return (
              row?.bookingChannel && <FormattedMessage id={`bookingsGrid.${row.bookingChannel}`} />
            );
          },
          outOfPolicy: (row) => {
            return row.outOfPolicy ? (
              <FormattedMessage id={'bookingsGrid.outOfPolicyTrue'} />
            ) : (
              <FormattedMessage id={'bookingsGrid.outOfPolicyFalse'} />
            );
          },
          reasonCode: (row) => {
            return row.reasonCode ? getReasonCodeDescription(row.reasonCode) : '-';
          },
          ticketNumber: (row) => {
            return row.ticketNumber ? String(row.ticketNumber) : '';
          },
          bookedByFirstName: (row) => {
            return getNameParts(row.bookedBy)[0];
          },
          bookedByLastName: (row) => {
            return getNameParts(row.bookedBy)[1];
          },
          provider: (row) => {
            return row?.provider && <FormattedMessage id={`reports.provider.${row.provider}`} />;
          },
        }}
        columnCustomWidth={{
          reasonCode: '250px !important',
          passengerName: '230px !important',
          recordLocator: '185px !important',
          groupName: '185px !important',
          ticketNumber: '200px !important',
          bookedByFirstName: '190px !important',
          bookedByLastName: '190px !important',
          firstName: '200px !important',
          lastName: '200px !important',
          bookingChannel: '190px !important',
          DEPARTMENT_NAME: '185px !important',
          EMPLOYEE_NUMBER: '185px !important',
          arrivalCountry: '185px !important',
        }}
        noDataComponent={<ReportsNoBookings />}
        showHeader={true}
        persistTableHead={false}
        onRowClick={onRowClick}
        handleSort={handleSort}
        defaultSort={defaultSort}
      />
    </div>
  );
}

export default BookingGrid;
